import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import PublicNavBar from '../../components/layout/PublicNavBar';
import PipelineStrip from '../../components/layout/PipelineStrip';
import PublicFooter from '../../components/layout/PublicFooter';
import ParameterStabilityHero from './optimization/ParameterStabilityHero';
import GridSearchMock from './optimization/GridSearchMock';
import SpikeVsPlateauVisual from './optimization/SpikeVsPlateauVisual';
import StabilityRefinementMock from './optimization/StabilityRefinementMock';
import CrossAssetMock from './optimization/CrossAssetMock';
import VersionHistoryMock from './optimization/VersionHistoryMock';
import WFABridgeCards from './optimization/WFABridgeCards';
import { usePageMeta } from '../../hooks/usePageMeta';

/* ═══════════════════════════ PAGE ═════════════════════════════════════ */

const OptimizationPage: FC = () => {
  usePageMeta({ title: 'Parameter Optimization', description: 'Optimization as stability analysis. Find robust parameter regions instead of curve-fitted peaks. Grid search, stability scoring, and cross-asset validation.' });

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins] selection:bg-[#9d4edd]/30">
      <PublicNavBar />

      {/* 1. HERO */}
      <section className="relative pt-24 sm:pt-36 pb-28 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1520] via-[#0a0e14] to-[#080b11]" />
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        <div className="max-w-[1440px] mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/60 mb-5">
              03 &mdash; Parameter Optimization
            </p>

            <h1 className="text-3xl sm:text-[2.25rem] md:text-[2.5rem] font-bold leading-[1.1] tracking-[-0.025em] mb-4 text-white">
              Optimization as stability analysis
            </h1>

            <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-3 max-w-[500px]">
              Optimization in Quanthop focuses on identifying stable parameter
              regions &mdash; contiguous areas of the search surface where
              performance remains consistent across neighbouring values and
              market regimes.
            </p>

            <p className="text-[14px] text-white/60 italic mb-5">
              The best parameter set is the one surrounded by other good parameter sets.
            </p>

            <p className="text-[12px] font-mono uppercase tracking-widest text-gray-500 mb-2">The research pipeline produces</p>
            <div className="space-y-2 mb-5">
              {[
                'Stable parameter regions identified through plateau detection',
                'Walk-forward and cross-asset validation at every stage',
                'Full version timeline with restore and comparison across iterations',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-[#9d4edd]/50 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-400 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 mb-5">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2.5 px-7 py-3 bg-[#9d4edd] hover:bg-[#8b3ec7] text-white rounded text-sm font-medium transition-colors"
              >
                Start Research
                <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
              </Link>
              <a href="#how-it-works" className="text-[13px] text-gray-400 hover:text-gray-200 transition-colors">
                How it works <span className="text-gray-600">&rarr;</span>
              </a>
            </div>

            <p className="text-[12px] text-gray-500 leading-snug max-w-[440px]">
              Outputs: parameter search surface, stable regions, cluster stability scores, and WFA-ready candidate sets.
            </p>
          </div>

          <ParameterStabilityHero />
        </div>
      </section>

      <PipelineStrip active="optimization" />

      {/* 2. HOW IT WORKS */}
      <section id="how-it-works" className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-xl md:text-[24px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              The parameter search pipeline
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] max-w-[560px] mx-auto">
              Three stages, each testing parameters from a different angle.
              Every stage gates the next &mdash; only stable results advance.
            </p>
          </div>

          <div className="space-y-0">
            {[
              { num: '01', title: 'Grid Search', text: 'Evaluates strategies across the full parameter surface. Every combination runs a backtest with realistic costs.', gate: 'Explore stability / Narrow search' },
              { num: '02', title: 'Robustness Analysis', text: 'Identifies stable parameter plateaus where nearby values produce similar results. Peaks without plateaus are discarded.', gate: 'Proceed to WFA / Redesign strategy' },
              { num: '03', title: 'Cross-Asset Validation', text: 'Tests parameter regions across multiple instruments simultaneously to verify they generalize, not just fit one dataset.', gate: 'Robust / Mixed / No stable region' },
            ].map((stage, i) => (
              <div key={stage.num} className="relative">
                {i < 2 && <div className="absolute left-[15px] top-[48px] w-px h-[calc(100%-20px)] bg-gray-300/40" />}
                {i < 2 && <div className="absolute left-[11px] top-[56px] text-gray-300/60 text-[10px] z-10">&darr;</div>}
                <div className="flex items-start gap-5 py-5">
                  <div className="w-[30px] h-[30px] rounded-lg bg-[#9d4edd]/10 border border-[#9d4edd]/20 flex items-center justify-center flex-shrink-0 relative z-10">
                    <span className="font-mono text-[11px] text-[#9d4edd] font-semibold">{stage.num}</span>
                  </div>
                  <div className="pt-0.5">
                    <h3 className="text-[15px] font-semibold text-gray-950 mb-0.5">{stage.title}</h3>
                    <p className="text-[13px] text-gray-500 leading-relaxed mb-2">{stage.text}</p>
                    <p className="text-[11px] text-[#9d4edd]/70 font-mono">Gate: {stage.gate}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. GRID SEARCH -- DEEP DIVE */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F0F2F5]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">Layer 01</p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Exhaustive parameter search
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Quanthop evaluates strategies across the full parameter surface.
              Every combination runs a backtest with realistic execution costs,
              producing a complete performance profile.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">How the search works:</p>
            <div className="space-y-2 mb-6">
              {[
                'Full parameter surface evaluation, not random sampling',
                'Metric aggregation per region to detect stable neighbourhoods',
                'Plateau detection over peak chasing -- wide stable areas, not sharp spikes',
                'Up to 50,000 combinations per search',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">Optimization targets:</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {['Sharpe Ratio', 'Total Return', 'Profit Factor', 'Win Rate', 'Max Drawdown'].map((target) => (
                <span key={target} className="text-[11px] bg-gray-200 text-gray-700 px-2.5 py-1 rounded font-mono">{target}</span>
              ))}
            </div>

            <p className="text-[14px] text-gray-900 leading-[1.7] font-medium mb-2">
              Fixed capital allocation prevents path-dependent scoring bias.
            </p>
            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Output: Full parameter search surface for every tested combination
            </p>
          </div>

          <GridSearchMock />
        </div>
      </section>

      {/* PARAMETER SURFACE CONCEPT */}
      <section className="py-14 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[800px] mx-auto text-center">
          <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">Core Concept</p>
          <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-4">
            Robust strategies form plateaus, not spikes
          </h2>
          <p className="text-[15px] text-gray-600 leading-[1.7] mb-8 max-w-[600px] mx-auto">
            Robust strategies form plateaus, where neighbouring parameter
            values produce consistent performance. Stability across
            parameter space is a strong signal that the underlying logic
            is structurally sound.
          </p>

          <SpikeVsPlateauVisual />

          <p className="text-[13px] text-gray-500 italic">
            The stability refinement stage identifies the second pattern and filters out the first.
          </p>
        </div>
      </section>

      {/* 4. ROBUSTNESS ANALYSIS -- DEEP DIVE */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-12 lg:gap-16 items-center">
          <StabilityRefinementMock />

          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">Layer 02</p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Stability refinement and region selection
            </h2>
            <p className="text-[15px] text-gray-800 leading-[1.7] mb-4 font-medium">
              Stability refinement identifies contiguous parameter regions
              where performance remains consistent across neighbouring
              values. These regions indicate structural robustness in the
              underlying strategy logic.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Only candidates drawn from stable regions proceed to
              walk-forward validation, which tests whether stability
              persists through time.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">What stable regions reveal:</p>
            <div className="space-y-2 mb-6">
              {[
                'Gradual performance decay = structural edge',
                'Sharp cliff = parameter-specific artefact',
                'Wide clusters = robust, ready for WFA',
                'Multiple clusters = regime-dependent behaviour',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[14px] text-gray-900 leading-[1.7] font-medium mb-2">
              Only candidates from stable regions are forwarded to walk-forward analysis.
            </p>
            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Gate: Proceed to WFA / Redesign strategy
            </p>
          </div>
        </div>
      </section>

      {/* 5. CROSS-ASSET BASELINE -- DEEP DIVE */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F0F2F5]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">Layer 03</p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Cross-asset parameter validation
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Cross-asset validation evaluates parameter structures across
              multiple instruments simultaneously to identify strategies
              that generalize across markets.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">What cross-asset validation measures:</p>
            <div className="space-y-2 mb-6">
              {[
                'Consistency: how evenly parameters perform across assets (0-100)',
                'Regime stability: whether parameter regions hold across market structures',
                'Generalization score: penalizes parameter sets with single-asset dependence',
                'Stable parameter region detection across all tested assets',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[14px] text-gray-900 leading-[1.7] font-medium mb-2">
              Parameter sets that maintain consistency across assets demonstrate
              stronger structural robustness.
            </p>
            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Gate: Robust / Mixed / No stable region
            </p>
          </div>

          <CrossAssetMock />
        </div>
      </section>

      {/* 6. VERSION HISTORY -- DEEP DIVE */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-12 lg:gap-16 items-center">
          <VersionHistoryMock />

          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">Research Versioning</p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Parameter lineage and research versioning
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Every strategy change creates a new version in the timeline.
              Each entry captures the version message, experiment count,
              and timestamp — giving a clear record of how your research
              evolves over successive iterations.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">What the version timeline provides:</p>
            <div className="space-y-2 mb-6">
              {[
                'Chronological timeline of every saved strategy version',
                'Experiment counts per version to track research depth',
                'One-click restore to any previous version',
                'Side-by-side code comparison between any two versions',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[14px] text-gray-900 leading-[1.7] font-medium mb-2">
              A full version history means you can always return to a known
              good state after any experimental change.
            </p>
            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Output: Version timeline with restore and compare actions
            </p>
          </div>
        </div>
      </section>

      {/* 7. RE-OPT BURDEN & WFA BRIDGE */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">Pipeline Integration</p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              From parameter discovery to strategy validation
            </h2>
            <p className="text-[15px] text-gray-800 leading-[1.7] mb-4 font-medium">
              The optimization engine forms the first stage of the strategy
              research pipeline. Stable parameters feed directly into
              walk-forward analysis for temporal verification.
            </p>

            {/* Pipeline flow visual */}
            <div className="flex flex-wrap items-center gap-2 mb-4 text-[11px] font-mono">
              {[
                { label: 'Grid Search', active: true },
                { label: 'Robustness', active: true },
                { label: 'Cross-Asset', active: true },
                { label: 'WFA', active: false },
                { label: 'Adaptive Flow', active: false },
              ].map((step, i, arr) => (
                <span key={step.label} className="flex items-center gap-2">
                  <span className={step.active ? 'text-[#9d4edd] font-semibold' : 'text-gray-500'}>
                    {step.label}
                  </span>
                  {i < arr.length - 1 && <span className="text-gray-400">&rarr;</span>}
                </span>
              ))}
            </div>

            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              The optimizer produces candidate parameter sets from stable
              regions. Walk-forward analysis tests these candidates across
              rolling time windows. Only strategies that pass both stages
              earn deployment eligibility.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">End-to-end pipeline:</p>
            <div className="space-y-2 mb-6">
              {[
                'Grid search maps the full parameter search surface',
                'Robustness analysis identifies stable clusters',
                'WFA candidates selected from parameter stability regions only',
                'Walk-forward validates temporal consistency',
                'Re-optimization burden assessed for maintenance cost',
                'Deployment readiness scored across all pillars',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[14px] text-gray-900 leading-[1.7] font-medium mb-2">
              The entire flow is automated. One-click from grid search to
              deployment readiness assessment.
            </p>
          </div>

          <WFABridgeCards />
        </div>
      </section>

      {/* 8. WHY STABILITY OVER OPTIMALITY */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F0F2F5]">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-xl md:text-[24px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Stability-driven parameter search
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] max-w-[560px] mx-auto">
              The optimization engine scores the neighbourhood around every
              parameter set, because the parameter you deploy will never
              behave exactly like the one you tested.
            </p>
          </div>

          <div className="max-w-[560px] mx-auto">
            <div className="rounded-lg border border-purple-200/80 bg-purple-50/50 px-6 py-6">
              <h3 className="text-[14px] font-semibold text-gray-950 mb-4">Method</h3>
              <div className="space-y-2.5">
                {[
                  'Identifies contiguous parameter regions across the search surface',
                  'Evaluates consistency across multiple assets simultaneously',
                  'Validates stability through walk-forward testing',
                  'Tracks research iterations with version timeline and restore',
                  'Scores deployment readiness across robustness, burden, and drift',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full mt-[6px] flex-shrink-0" style={{ background: '#9d4edd' }} />
                    <span className="text-[13px] text-gray-600 leading-snug">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 9. CTA */}
      <section className="relative py-28 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-[#9d4edd]/[0.03] to-transparent pointer-events-none" />
        <div className="max-w-[600px] mx-auto text-center relative z-10">
          <h2 className="text-xl md:text-[24px] font-bold text-white tracking-[-0.02em] mb-5">
            Find parameters that survive, not just perform
          </h2>
          <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-3">
            Limited research seats available. Start building with stability-first
            optimization.
          </p>
          <p className="text-[13px] text-gray-400/60 leading-[1.7] mb-4">
            Full parameter search surface. Cluster analysis. Cross-asset validation. Version tracking.
          </p>
          <p className="text-[13px] text-gray-400/80 leading-[1.7] mb-10 font-medium">
            A structured research pipeline for parameter stability analysis.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#9d4edd] hover:bg-[#8b3ec7] text-white rounded text-sm font-medium transition-colors"
          >
            Start Research
            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
          </Link>
          <div className="mt-4">
            <Link to="/features/validation" className="text-[13px] text-gray-400 hover:text-gray-200 transition-colors">
              Learn about the validation pipeline <span className="text-gray-600">&rarr;</span>
            </Link>
          </div>
        </div>
      </section>

      {/* NAV FOOTER */}
      <section className="py-12 px-4 sm:px-6 lg:px-10 border-t border-gray-700/40">
        <div className="max-w-[960px] mx-auto flex items-center justify-between">
          <Link to="/features/validation" className="inline-flex items-center gap-2 text-[13px] text-gray-500 hover:text-gray-300 transition-colors">
            <FontAwesomeIcon icon={faArrowLeft} className="text-[10px]" />
            Structured Validation
          </Link>
          <Link to="/features/continuous-validation" className="inline-flex items-center gap-2 text-[13px] text-[#9d4edd] hover:text-[#b266ff] transition-colors font-medium">
            Next: Continuous Validation
            <FontAwesomeIcon icon={faArrowRight} className="text-[11px]" />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default OptimizationPage;
